/**
 * Drops SSE events when the inbound rate exceeds a burst threshold, then
 * triggers a single full refresh after the storm subsides. Pairs with any
 * per-row debouncer that would otherwise enqueue thousands of HTTP refetches
 * during a bulk-sync fanout (the 17k-event `cache_cognito_users` reconcile
 * that crashed the browser).
 *
 * USAGE:
 *
 *   private _burst = new SseBurstBreaker({
 *     onPostStormRefresh: () => this.refresh(),
 *     // optional overrides; defaults are sized for the 17k-user storm
 *     windowMs: 2000, threshold: 50, quietMs: 1500,
 *   });
 *
 *   es.onmessage = (ev) => {
 *     if (this._burst.shouldDrop()) return;
 *     // existing per-row enqueue / parse
 *   };
 *
 *   ngOnDestroy() {
 *     this._burst.dispose();
 *   }
 *
 * SEMANTICS:
 *   - Counts events within a rolling `windowMs` window. When the count
 *     crosses `threshold`, enters suppress mode and starts dropping.
 *   - While suppressing, every incoming event resets a quiet timer
 *     (`quietMs`). When the quiet timer fires (no events for `quietMs`),
 *     exits suppress mode and invokes `onPostStormRefresh` exactly once.
 *   - During normal (low-rate) operation `shouldDrop()` returns false and
 *     events flow through unmodified.
 */
export interface SseBurstBreakerOptions {
  /** Callback invoked once when the storm subsides. Typically `() => this.refresh()`. */
  onPostStormRefresh: () => void | Promise<void>;
  /** Observation window in ms. Default 2000. */
  windowMs?: number;
  /** Event count within `windowMs` that triggers suppress mode. Default 50. */
  threshold?: number;
  /** Quiet period after last event before exiting suppress mode + refreshing. Default 1500. */
  quietMs?: number;
  /** Optional structured logger for suppress-on / suppress-off transitions. */
  onTransition?: (state: 'suppress-on' | 'suppress-off', counters: { eventsDropped: number; eventsObserved: number }) => void;
}

export class SseBurstBreaker {
  private readonly windowMs: number;
  private readonly threshold: number;
  private readonly quietMs: number;
  private readonly onPostStormRefresh: () => void | Promise<void>;
  private readonly onTransition?: (state: 'suppress-on' | 'suppress-off', counters: { eventsDropped: number; eventsObserved: number }) => void;

  private _windowStart: number | null = null;
  private _eventsInWindow: number = 0;
  private _suppressing: boolean = false;
  private _eventsDroppedThisStorm: number = 0;
  private _quietTimer: any = null;

  constructor(opts: SseBurstBreakerOptions) {
    this.onPostStormRefresh = opts.onPostStormRefresh;
    this.windowMs = opts.windowMs ?? 2000;
    this.threshold = opts.threshold ?? 50;
    this.quietMs = opts.quietMs ?? 1500;
    this.onTransition = opts.onTransition;
  }

  /**
   * Returns true when this event should be dropped (rate-limit). Caller
   * should `return` early without dispatching to its per-row handler.
   * Side-effect: in suppress mode, extends the quiet timer.
   */
  shouldDrop(): boolean {
    const now = Date.now();
    // Already in suppress mode: drop everything, just keep the quiet timer alive.
    if (this._suppressing) {
      this._eventsDroppedThisStorm++;
      this._armQuietTimer();
      return true;
    }
    // Sliding window count.
    if (this._windowStart === null || now - this._windowStart > this.windowMs) {
      this._windowStart = now;
      this._eventsInWindow = 1;
    } else {
      this._eventsInWindow++;
    }
    // Crossed the burst threshold — flip into suppress mode and drop this event too.
    if (this._eventsInWindow > this.threshold) {
      this._suppressing = true;
      this._eventsDroppedThisStorm = 1;
      this._armQuietTimer();
      this.onTransition?.('suppress-on', {
        eventsDropped: this._eventsDroppedThisStorm,
        eventsObserved: this._eventsInWindow,
      });
      return true;
    }
    return false;
  }

  /** True while in suppress mode. Useful for UI indicators. */
  isSuppressing(): boolean {
    return this._suppressing;
  }

  /** Cancel timers. Call from ngOnDestroy. */
  dispose(): void {
    if (this._quietTimer) {
      clearTimeout(this._quietTimer);
      this._quietTimer = null;
    }
  }

  private _armQuietTimer() {
    if (this._quietTimer) clearTimeout(this._quietTimer);
    this._quietTimer = setTimeout(() => {
      this._quietTimer = null;
      const dropped = this._eventsDroppedThisStorm;
      const observed = this._eventsInWindow;
      this._suppressing = false;
      this._eventsInWindow = 0;
      this._windowStart = null;
      this._eventsDroppedThisStorm = 0;
      this.onTransition?.('suppress-off', { eventsDropped: dropped, eventsObserved: observed });
      try {
        const r = this.onPostStormRefresh();
        if (r && typeof (r as any).catch === 'function') {
          (r as Promise<void>).catch(() => { /* refresh errors are caller's to handle */ });
        }
      } catch { /* ignore */ }
    }, this.quietMs);
  }
}
